import { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Box,
  Badge,
  StatusIndicator,
  Header,
  SpaceBetween,
} from '@cloudscape-design/components';
import { getAuditLog, getFeedback } from '../services/s3Service';
import { resolveProgram } from '../programs/registry';
import { getReplyDispositionLabel } from '../utils/feedbackUtils';

const DECISION_COLORS = {
  'RECOMMEND_APPROVAL': 'green',
  'RECOMMEND_REJECTION': 'red',
  'NEEDS_REVIEW': 'blue',
};

const formatTimestamp = (ts) => {
  if (!ts) return '-';
  return new Date(ts).toLocaleString();
};

/**
 * Build a single timeline from audit-log validation records and feedback records.
 * Service account entries (canary re-validations) are excluded from the UI.
 */
const SERVICE_ACCOUNTS = ['svc-chronos-prd-fed@amazon.com'];

const buildTimeline = (auditRecords, feedbackItems, decision) => {
  const events = [];

  // --- Validation events from audit log ---
  // Only include meaningful state transitions (skip VIEW/VISIT noise)
  const validationOps = new Set(['INSERT', 'MODIFY']);
  for (const rec of auditRecords) {
    if (!validationOps.has(rec.operation)) continue;
    const reviewState =
      rec.new_image?.review_state?.S || rec.new_image?.review_state || '';
    if (!reviewState) continue;

    // Skip service account entries (canary automated re-validations)
    const user = rec.user || 'System';
    if (SERVICE_ACCOUNTS.includes(user)) continue;

    // For failed validations show "Failed"; otherwise show the validation decision
    const isFailed = reviewState === '4-Failed';
    const status = isFailed ? 'Failed' : (decision || 'Completed');

    events.push({
      timestamp: rec.timestamp,
      type: 'Validation',
      status,
      details: rec.operation === 'INSERT' ? 'Initial validation' : 'Re-validation',
      user,
    });
  }

  // --- Feedback events ---
  for (const fb of feedbackItems) {
    const ts = fb.updatedAt || fb.submittedAt;
    let details = '';
    if (fb.feedbackType === 'DISAGREE') {
      const parts = [];
      if (fb.recommendedDecision) parts.push(`Recommended: ${fb.recommendedDecision}`);
      if (fb.disagreementReason) parts.push(fb.disagreementReason);
      details = parts.join(' — ');
    }
    if (fb.replyDisposition) {
      details += details
        ? ` | Reply: ${getReplyDispositionLabel(fb.replyDisposition)}`
        : `Reply: ${getReplyDispositionLabel(fb.replyDisposition)}`;
      if (fb.replyExplanation) details += ` — ${fb.replyExplanation}`;
    }

    events.push({
      timestamp: ts,
      type: 'Feedback',
      status: fb.feedbackType || '-',
      details,
      user: fb.reviewerAlias || fb.reviewerId || '-',
    });
  }

  // Sort descending by timestamp
  events.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  return events;
};

const COLUMN_DEFINITIONS = [
  {
    id: 'date',
    header: 'Date',
    cell: (item) => formatTimestamp(item.timestamp),
    width: 200,
  },
  {
    id: 'type',
    header: 'Type',
    cell: (item) => (
      <Badge color={item.type === 'Validation' ? 'blue' : 'green'}>
        {item.type}
      </Badge>
    ),
    width: 120,
  },
  {
    id: 'status',
    header: 'Status',
    cell: (item) => {
      if (item.type === 'Validation') {
        if (item.status === 'Failed') {
          return <StatusIndicator type="error">Failed</StatusIndicator>;
        }
        const color = DECISION_COLORS[item.status] || 'blue';
        return <Badge color={color}>{item.status.replace(/_/g, ' ')}</Badge>;
      }
      // Feedback
      return (
        <Badge color={item.status === 'AGREE' ? 'green' : 'red'}>
          {item.status}
        </Badge>
      );
    },
    width: 200,
  },
  {
    id: 'details',
    header: 'Details',
    cell: (item) => item.details || '-',
  },
  {
    id: 'user',
    header: 'User',
    cell: (item) => item.user,
    width: 200,
  },
];

const HistoryTab = ({ fundRequestId, request }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadHistory = useCallback(async () => {
    if (!fundRequestId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);

      const [auditResponse, feedbackResponse] = await Promise.all([
        getAuditLog({
          recordKey: fundRequestId,
          tableName: 'chronos_review_state',
          pageSize: 100,
        }),
        getFeedback(fundRequestId).catch(() => ({ allFeedback: [] })),
      ]);

      const auditRecords = auditResponse.auditRecords || [];
      // allFeedback is populated for admins; for regular users, fall back to
      // their own single feedback item so the timeline is never empty.
      let feedbackItems = feedbackResponse.allFeedback || [];
      if (feedbackItems.length === 0 && feedbackResponse.feedback) {
        feedbackItems = [feedbackResponse.feedback];
      }

      // Rubicon's approval_summary.decision is the short internal code
      // ("approve"/"reject"/"escalate"), not the RECOMMEND_APPROVAL /
      // RECOMMEND_REJECTION / NEEDS_REVIEW vocabulary DECISION_COLORS and
      // the badge text expect (MAP/WMP/IW already persist .decision in that
      // display-ready form). For Rubicon, read .recommendation instead
      // (already "recommend_approval"/"recommend_rejection"/"needs_review")
      // and uppercase it to match. Other programs are unaffected.
      const isRubicon = resolveProgram(request) === 'RUBICON';
      const decisionForTimeline = isRubicon
        ? request?.approval_summary?.recommendation?.toUpperCase()
        : request?.approval_summary?.decision;
      setItems(buildTimeline(auditRecords, feedbackItems, decisionForTimeline));
    } catch (err) {
      console.error('Failed to load history:', err);
      setError(err.message || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  }, [fundRequestId, request]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  return (
    <Table
      header={
        <Header
          counter={`(${items.length})`}
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Box variant="small" color="text-body-secondary" padding={{ top: 'xs' }}>
                Validation runs and reviewer feedback
              </Box>
            </SpaceBetween>
          }
        >
          History
        </Header>
      }
      columnDefinitions={COLUMN_DEFINITIONS}
      items={items}
      loading={loading}
      loadingText="Loading history..."
      sortingDisabled
      variant="container"
      empty={
        <Box textAlign="center" color="inherit" padding="l">
          {error ? (
            <StatusIndicator type="error">{error}</StatusIndicator>
          ) : (
            <>
              <b>No history</b>
              <Box variant="p" color="inherit">
                No validation or feedback events found for this fund request.
              </Box>
            </>
          )}
        </Box>
      }
    />
  );
};

export default HistoryTab;
